import JSZip from 'jszip';

const KEYS = ['customReplies', 'customReplyGroups', 'disabledReplyItems'];

export async function parseMilkFile(file) {
  if (!file) throw new Error('没有选择文件');
  const isZip = /\.zip$/i.test(file.name) || file.type === 'application/zip';
  let payload;

  if (isZip) {
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const backup = zip.file('backup.json') || Object.values(zip.files).find((entry) => !entry.dir && /\.json$/i.test(entry.name));
    if (!backup) throw new Error('ZIP 中没有找到 backup.json');
    payload = parseFlexibleJSON(await backup.async('string'));
  } else {
    payload = parseFlexibleJSON(await file.text());
  }

  return extractMilkData(payload, { fileName: file.name, source: isZip ? 'milk 全量 ZIP' : 'milk JSON' });
}

export function parseFlexibleJSON(text) {
  const clean = String(text ?? '').replace(/^\uFEFF/, '').trim();
  try {
    return JSON.parse(clean);
  } catch (_) {
    const repaired = clean.replace(/,\s*([}\]])/g, '$1');
    try {
      return JSON.parse(repaired);
    } catch (error) {
      throw new Error(`无法解析 JSON：${error.message}`);
    }
  }
}

export function extractMilkData(payload, meta = {}) {
  if (Array.isArray(payload)) {
    return makeImportPayload({ customReplies: payload }, { ...meta, format: '旧版纯数组' });
  }
  if (!payload || typeof payload !== 'object') throw new Error('文件中没有可识别的字卡数据');

  const direct = readKnownFields(payload);
  if (direct.customReplies) {
    return makeImportPayload(direct, { ...meta, format: detectFormat(payload) });
  }

  const storageCandidate = payload.localforage || payload.indexedDB || payload.storage || null;
  if (storageCandidate && typeof storageCandidate === 'object') {
    const fromStorage = readStorageMap(storageCandidate);
    const disabledFromStorage = readStorageMap(payload.localStorage || {});
    if (fromStorage.customReplies) {
      return makeImportPayload(
        { ...fromStorage, disabledReplyItems: disabledFromStorage.disabledReplyItems },
        { ...meta, format: payload.type || 'milk 全量备份' },
      );
    }
  }

  const deep = {};
  KEYS.forEach((key) => {
    deep[key] = findDeepValue(payload, key);
  });
  if (deep.customReplies) return makeImportPayload(deep, { ...meta, format: '嵌套 milk 数据' });

  throw new Error('没有检测到 customReplies 字卡数组');
}

export function mergeMilkData(current, incoming, mode = 'merge') {
  const hasGroups = incoming.hasGroups !== false;
  const hasDisabled = incoming.hasDisabled === true;

  if (mode === 'overwrite') {
    return {
      ...current,
      customReplies: [...incoming.customReplies],
      customReplyGroups: hasGroups ? cloneGroups(incoming.customReplyGroups) : current.customReplyGroups,
      disabledReplyItems: hasDisabled ? [...incoming.disabledReplyItems] : current.disabledReplyItems,
    };
  }

  const customReplies = dedupeMilk([...current.customReplies, ...incoming.customReplies]).result;
  const customReplyGroups = cloneGroups(current.customReplyGroups);
  incoming.customReplyGroups.forEach((incomingGroup) => {
    if (!customReplyGroups.some((group) => group.name === incomingGroup.name)) {
      customReplyGroups.push({ ...incomingGroup, items: [...incomingGroup.items] });
    }
  });

  return {
    ...current,
    customReplies,
    customReplyGroups,
    disabledReplyItems: [...new Set([...current.disabledReplyItems, ...incoming.disabledReplyItems])],
  };
}

export function dedupeMilk(items) {
  const seen = new Set();
  const result = [];
  let removedCount = 0;

  items.forEach((item) => {
    const normalized = normalizeMilkString(item);
    if (!normalized || seen.has(normalized)) {
      removedCount += 1;
      return;
    }
    seen.add(normalized);
    result.push(item);
  });

  return { result, removedCount };
}

export function createMilkExport(state) {
  return {
    exportDate: new Date().toISOString(),
    modules: ['replies', 'groups'],
    customReplies: [...state.customReplies],
    customReplyGroups: cloneGroups(state.customReplyGroups),
    disabledReplyItems: [...state.disabledReplyItems],
    _echoDiary: { version: 1, compatibleWith: 'aielin17/milk' },
  };
}

function makeImportPayload(candidate, meta) {
  const customReplies = decodeValue(candidate.customReplies);
  if (!Array.isArray(customReplies)) throw new Error('customReplies 不是数组');
  const groupsValue = decodeValue(candidate.customReplyGroups);
  const disabledValue = decodeValue(candidate.disabledReplyItems);

  return {
    customReplies: customReplies.filter((item) => typeof item === 'string'),
    customReplyGroups: Array.isArray(groupsValue) ? sanitizeGroups(groupsValue) : [],
    disabledReplyItems: Array.isArray(disabledValue) ? disabledValue.filter((item) => typeof item === 'string') : [],
    hasGroups: Array.isArray(groupsValue),
    hasDisabled: Array.isArray(disabledValue),
    meta,
  };
}

function readKnownFields(object) {
  const output = {};
  KEYS.forEach((key) => {
    if (object[key] !== undefined && object[key] !== null) output[key] = decodeValue(object[key]);
  });
  return output;
}

function readStorageMap(storage) {
  const output = {};
  Object.entries(storage || {}).forEach(([storageKey, raw]) => {
    KEYS.forEach((key) => {
      if (output[key] !== undefined) return;
      if (storageKey === key || storageKey.endsWith(`_${key}`) || storageKey.endsWith(`:${key}`)) {
        output[key] = decodeValue(raw);
      }
    });
  });
  return output;
}

function findDeepValue(value, targetKey, depth = 0, visited = new Set()) {
  if (!value || typeof value !== 'object' || depth > 6 || visited.has(value)) return undefined;
  visited.add(value);
  if (Object.prototype.hasOwnProperty.call(value, targetKey)) return decodeValue(value[targetKey]);
  for (const child of Object.values(value)) {
    const found = findDeepValue(child, targetKey, depth + 1, visited);
    if (found !== undefined) return found;
  }
  return undefined;
}

function decodeValue(value) {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
}

function detectFormat(payload) {
  if (payload._groupExport) return 'milk 分组导出';
  if (Array.isArray(payload.modules)) return 'milk 字卡库导出';
  if (payload.version || payload.exportModules) return `milk 聊天备份 ${payload.version || ''}`.trim();
  return 'milk 字卡 JSON';
}

function sanitizeGroups(groups) {
  return groups
    .filter((group) => group && typeof group === 'object')
    .map((group, index) => ({
      id: group.id ?? `imported-${Date.now()}-${index}`,
      name: String(group.name || `未命名分组 ${index + 1}`).trim(),
      color: /^#[0-9a-f]{6}$/i.test(group.color) ? group.color : '#7a6854',
      disabled: Boolean(group.disabled),
      items: Array.isArray(group.items) ? group.items.filter((item) => typeof item === 'string') : [],
    }));
}

function cloneGroups(groups) {
  return (groups || []).map((group) => ({ ...group, items: [...(group.items || [])] }));
}

function normalizeMilkString(value) {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
}
